import { getProductPageUrl } from '@constants'
import { useBusinessContext } from '@context'
import { useQueryParam } from '@hooks'
import { IProduct } from '@query'
import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import useVirtual from 'react-cool-virtual'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { Badge } from '@components'
import { useTranslation } from 'react-i18next'
import { useScreen } from '@utils'
import { useVATCode } from '@root/query/hooks/useVATCodes'

const ESTIMATED_ITEM_HEIGHT = 79
const SCROLL_BOTTOM_THRESHOLD = 5

interface ProductListProps {
  products: IProduct[]
  onScrollBottom?: () => void
}

export const ProductList: React.FC<ProductListProps> = ({ products, onScrollBottom }) => {
  const [selectedId] = useQueryParam<number>('id', value => parseInt(value, 10))

  const { outerRef, innerRef, items } = useVirtual<HTMLDivElement, HTMLDivElement>({
    itemCount: products.length,
    itemSize: ESTIMATED_ITEM_HEIGHT,
    onScroll: ({ visibleStopIndex }) => {
      if (onScrollBottom && visibleStopIndex >= products.length - SCROLL_BOTTOM_THRESHOLD) {
        onScrollBottom()
      }
    }
  })

  return (
    <ProductListScrollable ref={outerRef}>
      <div ref={innerRef}>
        <AnimatePresence>
          {items.map(({ index, measureRef }) => {
            const product = products[index]
            if (!product) return null
            return (
              <motion.div key={product.id} ref={el => measureRef(el)} layout="position">
                <ProductListItem
                  product={product}
                  isSelected={selectedId === product.id}
                  isFirst={index === 0}
                  isLast={index === products.length - 1}
                />
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
    </ProductListScrollable>
  )
}

interface ProductItemProps {
  product: IProduct
  isSelected: boolean
  isFirst: boolean
  isLast: boolean
}

const ProductListItem: React.FC<ProductItemProps> = ({ product, isFirst, isLast, isSelected }) => {
  const { slug } = useBusinessContext()
  const { isDesktop } = useScreen()
  const { t } = useTranslation()
  const vatOption = useVATCode(product.vat_code)

  return (
    <ProductItemWrapper
      to={getProductPageUrl(slug, { id: product.id })}
      className={cn({ isSelected, isFirst, isLast })}
    >
      <Row>
        <ProductCode>{product.code || '-'}</ProductCode>
        {isDesktop && (
          <Badges>
            {product.vat_code !== 3 && (
              <>
                {vatOption && <Badge type="success">{vatOption.label}</Badge>}
                <Badge type="info">
                  {t('accounts.overview.vatValue', { vatRate: product.vat_rate })}
                </Badge>
              </>
            )}
          </Badges>
        )}
      </Row>
      <Row>
        <ProductName>{product.name}</ProductName>
      </Row>
    </ProductItemWrapper>
  )
}

const ProductListScrollable = styled.div`
  overflow: auto;
  flex: 1;
  height: 100%;
`

const ProductItemWrapper = styled(Link)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-bottom: 0.5px solid #f6f7f9;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-height: ${ESTIMATED_ITEM_HEIGHT}px;

  &.isFirst {
    border-top-left-radius: ${({ theme }) => theme.borderRadius.md}rem;
    border-top-right-radius: ${({ theme }) => theme.borderRadius.md}rem;
  }

  &.isLast {
    border-bottom-left-radius: ${({ theme }) => theme.borderRadius.md}rem;
    border-bottom-right-radius: ${({ theme }) => theme.borderRadius.md}rem;
  }

  &:hover,
  &.isSelected {
    text-decoration: none;
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

const ProductCode = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const ProductName = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const Badges = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`
